import React, { useState } from 'react';
import { FormSection, FormResponses } from '@/lib/forms/schema';
import { FormFieldRenderer } from './FormFieldRenderer';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface FormSectionRendererProps {
  section: FormSection;
  responses: FormResponses;
  onChange: (key: string, value: any) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
}

export function FormSectionRenderer({
  section,
  responses,
  onChange,
  errors = {},
  disabled = false,
}: FormSectionRendererProps) {
  const [isOpen, setIsOpen] = useState(!section.defaultCollapsed);

  // Filter fields based on conditional logic
  const visibleFields = section.fields.filter((field) => {
    if (!field.conditional) return true;
    
    const { field: condField, operator = 'equals', value: condValue } = field.conditional;
    const currentValue = responses[condField];

    switch (operator) {
      case 'equals':
        return currentValue === condValue;
      case 'notEquals':
        return currentValue !== condValue;
      case 'contains':
        if (Array.isArray(condValue)) {
          return condValue.includes(currentValue as string);
        }
        return String(currentValue || '').includes(String(condValue));
      case 'exists':
        return currentValue !== undefined && currentValue !== null && currentValue !== '';
      default:
        return currentValue === condValue;
    }
  });

  // Don't render section if no visible fields
  if (visibleFields.length === 0) return null;

  const content = (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {visibleFields.map((field) => (
        <FormFieldRenderer
          key={field.key}
          field={field}
          value={responses[field.key]}
          onChange={onChange}
          error={errors[field.key]}
          disabled={disabled}
        />
      ))}
    </div>
  );

  if (section.collapsible) {
    return (
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="border rounded-lg overflow-hidden">
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center justify-between p-4 bg-muted/50 hover:bg-muted/70 transition-colors">
              <div className="text-left">
                <h3 className="text-lg font-semibold">{section.title}</h3>
                {section.description && (
                  <p className="text-sm text-muted-foreground mt-1">{section.description}</p>
                )}
              </div>
              {isOpen ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="p-4">{content}</div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="p-4 bg-muted/50">
        <h3 className="text-lg font-semibold">{section.title}</h3>
        {section.description && (
          <p className="text-sm text-muted-foreground mt-1">{section.description}</p>
        )}
      </div>
      <div className="p-4">{content}</div>
    </div>
  );
}
